import type { LoadCalcTemplate, LoadCalcTemplateId, LoadCalcValues } from "./loadCalcTypes";

const hazard = {
  id: "hazard",
  label: "Site & hazard",
  summary: "Code basis and site parameters",
  openByDefault: true,
} as const;

const geometry = {
  id: "geometry",
  label: "Geometry",
  summary: "Dimensions and load regions",
  openByDefault: true,
} as const;

export const LOAD_CALC_TEMPLATES: readonly LoadCalcTemplate[] = [
  {
    id: "wind_mwfrs",
    name: "Wind — MWFRS",
    shortName: "Wind MWFRS",
    family: "Wind",
    standard: "ASCE 7-22 Ch. 26–28",
    description: "Directional and low-rise envelope pressures for the main wind-force-resisting system.",
    visual: "wind-elevation",
    sourceContract: "loading_engine.wind_directional / wind_envelope",
    cases: ["Normal to ridge", "Parallel to ridge", "+GCpi", "−GCpi"],
    inputs: [
      { ...hazard, fields: [
        { key: "designWindSpeed_mph", label: "Design wind speed", type: "number", unit: "mph", value: 115 },
        { key: "riskCategory", label: "Risk category", type: "select", options: ["I", "II", "III", "IV"], value: "II" },
        { key: "exposureCategory", label: "Exposure", type: "select", options: ["B", "C", "D"], value: "C" },
        { key: "enclosureClassification", label: "Enclosure", type: "select", options: ["Enclosed", "Partially enclosed", "Open"], value: "Enclosed" },
        { key: "siteElevation_ft", label: "Site elevation", type: "number", unit: "ft", value: 0 },
        { key: "Kd", label: "Directionality Kd", type: "number", value: 0.85 },
        { key: "Kzt", label: "Topographic Kzt", type: "number", value: 1 },
      ] },
      { ...geometry, fields: [
        { key: "buildingLength_ft", label: "Building length", type: "number", unit: "ft", value: 48 },
        { key: "buildingWidth_ft", label: "Building width", type: "number", unit: "ft", value: 30 },
        { key: "meanRoofHeight_ft", label: "Mean roof height", type: "number", unit: "ft", value: 22 },
        { key: "roofPitch", label: "Roof pitch", type: "number", unit: ":12", value: 6 },
        { key: "roofType", label: "Roof type", type: "select", options: ["Gable", "Hip", "Monoslope", "Flat"], value: "Gable" },
        { key: "procedure", label: "Procedure", type: "select", options: ["Directional", "Envelope", "Governing of both"], value: "Directional" },
      ] },
      { id: "classification", label: "Classification & policy", summary: "Normals, minimums, and supplemental scope", fields: [
        { key: "minimumPressure", label: "Enforce minimum pressure", type: "bool", value: true },
        { key: "includeOverhangs", label: "Include overhangs", type: "bool", value: true },
        { key: "tornadoRequired", label: "Tornado procedure required", type: "bool", value: false },
      ] },
    ],
    intermediates: ["Velocity pressure qh", "External pressure coefficients", "Internal-pressure cases", "Minimum-pressure enforcement"],
    results: [{ id: "pressures", label: "Governing surface pressures", description: "Engine-authored pressure envelope by direction and surface.", columns: [
      { key: "surface", label: "Surface" }, { key: "direction", label: "Direction" }, { key: "inward", label: "Inward", unit: "psf" }, { key: "outward", label: "Outward", unit: "psf" }, { key: "case", label: "Governing case" },
    ], rowLabels: ["Windward wall", "Leeward wall", "Side wall", "Windward roof", "Leeward roof"] }],
    assumptions: ["Building axes and outward surface normals come from the approved geometry handoff.", "Pressure signs follow the loading-engine surface convention."],
    deferred: ["Automation geometry binding", "CAD/PDF review rendering"],
  },
  {
    id: "wind_cc",
    name: "Wind — Components & Cladding",
    shortName: "Wind C&C",
    family: "Wind",
    standard: "ASCE 7-22 Ch. 30",
    description: "Zone pressures for wall, roof, fastener, sheathing, and overhang components.",
    visual: "wind-zones",
    sourceContract: "loading_engine.wind_cc.CCResult",
    cases: ["Roof zones 1–3", "Wall zones 4–5", "Overhang"],
    inputs: [
      { ...hazard, fields: [
        { key: "designWindSpeed_mph", label: "Design wind speed", type: "number", unit: "mph", value: 115 },
        { key: "riskCategory", label: "Risk category", type: "select", options: ["I", "II", "III", "IV"], value: "II" },
        { key: "exposureCategory", label: "Exposure", type: "select", options: ["B", "C", "D"], value: "C" },
        { key: "enclosureClassification", label: "Enclosure", type: "select", options: ["Enclosed", "Partially enclosed", "Open"], value: "Enclosed" },
        { key: "siteElevation_ft", label: "Site elevation", type: "number", unit: "ft", value: 0 },
        { key: "Kd", label: "Directionality Kd", type: "number", value: 0.85 },
        { key: "Kzt", label: "Topographic Kzt", type: "number", value: 1 },
      ] },
      { ...geometry, fields: [
        { key: "meanRoofHeight_ft", label: "Mean roof height", type: "number", unit: "ft", value: 22 },
        { key: "leastHorizontalDimension_ft", label: "Least plan dimension", type: "number", unit: "ft", value: 30 },
        { key: "roofPitch", label: "Roof pitch", type: "number", unit: ":12", value: 6 },
        { key: "roofType", label: "Roof type", type: "select", options: ["Gable", "Hip", "Monoslope", "Flat"], value: "Gable" },
      ] },
      { id: "scope", label: "Display scope", summary: "Components and special surfaces", fields: [
        { key: "components", label: "Components", type: "table", addLabel: "Add component", minimumRows: 1, value: [
          { component: "Roof sheathing", effectiveArea_sqft: 32 },
        ], columns: [
          { key: "component", label: "Component", type: "select", options: ["Fastener", "Wall sheathing", "Roof sheathing", "Cladding", "Stud wall", "Roof rafter", "Window", "Door"], value: "Roof sheathing" },
          { key: "effectiveArea_sqft", label: "Effective area", type: "number", unit: "ft²", value: 10 },
        ] },
        { key: "showRoof", label: "Roof zones", type: "bool", value: true },
        { key: "showWalls", label: "Wall zones", type: "bool", value: true },
        { key: "showOverhangs", label: "Overhang pressures", type: "bool", value: true },
      ] },
    ],
    intermediates: ["Edge-strip width a", "Velocity pressure qh", "GCp by effective area", "GCpi case comparison"],
    results: [{ id: "zones", label: "Zone pressure schedule", description: "Positive and negative C&C pressures for each engine-provided zone.", columns: [
      { key: "component", label: "Component" }, { key: "zone", label: "Zone" }, { key: "area", label: "Area", unit: "ft²" }, { key: "inward", label: "Inward", unit: "psf" }, { key: "outward", label: "Outward", unit: "psf" },
    ], rowLabels: ["Roof zone 1", "Roof zone 2", "Roof zone 3", "Wall zone 4", "Wall zone 5"] }],
    assumptions: ["Effective wind area is component-specific.", "Zone geometry is taken from approved building dimensions."],
    deferred: ["Plan-bound zone polygons", "CAD/PDF review rendering"],
  },
  {
    id: "snow",
    name: "Snow — Balanced, Unbalanced & Drift",
    shortName: "Snow",
    family: "Snow",
    standard: "ASCE 7-22 Ch. 7",
    description: "Balanced roof snow, unbalanced cases, roof-step drift, and obstruction drift in one case workspace.",
    visual: "snow-roof",
    sourceContract: "loading_engine.snow_phenomenon.SnowPhenomenonResult",
    cases: ["Balanced", "Unbalanced", "Leeward drift", "Windward drift", "Obstruction"],
    inputs: [
      { ...hazard, fields: [
        { key: "pg_psf", label: "Ground snow pg", type: "number", unit: "psf", value: 30 },
        { key: "w2", label: "Winter wind speed", type: "number", unit: "mph", value: 45 },
        { key: "riskCategory", label: "Risk category", type: "select", options: ["I", "II", "III", "IV"], value: "II" },
        { key: "roughnessCategory", label: "Surface roughness", type: "select", options: ["B", "C", "D"], value: "B" },
        { key: "roofExposureClass", label: "Roof exposure", type: "select", options: ["Fully exposed", "Partially exposed", "Sheltered"], value: "Partially exposed" },
        { key: "thermalCondition", label: "Thermal condition", type: "select", options: ["Heated", "Unheated", "Continuously heated greenhouse"], value: "Heated" },
        { key: "surfaceCondition", label: "Roof surface", type: "select", options: ["Unobstructed slippery", "Other"], value: "Other" },
      ] },
      { ...geometry, fields: [
        { key: "roofPlanes", label: "Roof planes", type: "table", addLabel: "Add roof plane", minimumRows: 1, value: [
          { roofPlaneId: "Roof A", roofLength_ft: 42, eaveToRidge_ft: 16, roofPitch: 6, roofShape: "Gable", simplySupported: true },
          { roofPlaneId: "Roof B", roofLength_ft: 42, eaveToRidge_ft: 18, roofPitch: 6, roofShape: "Gable", simplySupported: true },
        ], columns: [
          { key: "roofPlaneId", label: "Plane", type: "text", value: "Roof" },
          { key: "roofLength_ft", label: "Length", type: "number", unit: "ft", value: 30 },
          { key: "eaveToRidge_ft", label: "Eave-ridge", type: "number", unit: "ft", value: 15 },
          { key: "roofPitch", label: "Pitch", type: "number", unit: ":12", value: 6 },
          { key: "roofShape", label: "Shape", type: "select", options: ["Gable", "Hip", "Monoslope", "Flat"], value: "Gable" },
          { key: "simplySupported", label: "Simple", type: "bool", value: true },
        ] },
      ] },
      { id: "drift", label: "Drift geometry", summary: "Adjacent roofs and obstructions", openByDefault: true, fields: [
        { key: "adjacencies", label: "Roof-step adjacencies", type: "table", addLabel: "Add adjacency", value: [
          { adjacencyId: "Step A", upperRoofPlaneId: "Roof A", lowerRoofPlaneId: "Roof B", stepHeight_ft: 4, upperFetch_ft: 26, lowerFetch_ft: 18 },
        ], columns: [
          { key: "adjacencyId", label: "ID", type: "text", value: "Step" },
          { key: "upperRoofPlaneId", label: "Upper plane", type: "text", value: "Roof A" },
          { key: "lowerRoofPlaneId", label: "Lower plane", type: "text", value: "Roof B" },
          { key: "stepHeight_ft", label: "Step", type: "number", unit: "ft", value: 4 },
          { key: "upperFetch_ft", label: "Upper fetch", type: "number", unit: "ft", value: 20 },
          { key: "lowerFetch_ft", label: "Lower fetch", type: "number", unit: "ft", value: 20 },
        ] },
        { key: "obstructions", label: "Obstructions", type: "table", addLabel: "Add obstruction", value: [
          { obstructionId: "Penthouse A", hostRoofPlaneId: "Roof A", height_ft: 2, upwindFetch_ft: 18 },
        ], columns: [
          { key: "obstructionId", label: "ID", type: "text", value: "Obstruction" },
          { key: "hostRoofPlaneId", label: "Roof plane", type: "text", value: "Roof A" },
          { key: "height_ft", label: "Height", type: "number", unit: "ft", value: 2 },
          { key: "upwindFetch_ft", label: "Fetch", type: "number", unit: "ft", value: 18 },
        ] },
        { key: "rainOnSnow", label: "Rain-on-snow applicable", type: "bool", value: false },
      ] },
    ],
    intermediates: ["Exposure factor Ce", "Thermal factor Ct", "Flat-roof snow pf", "Sloped-roof factor Cs", "Drift height and width screens", "Reliability-targeted factors"],
    results: [
      { id: "snow-cases", label: "Roof snow cases", description: "Calculated snow pressures by plane and phenomenon.", columns: [
        { key: "plane", label: "Plane" }, { key: "case", label: "Case" }, { key: "base", label: "Base", unit: "psf" }, { key: "peak", label: "Peak", unit: "psf" }, { key: "width", label: "Width", unit: "ft" }, { key: "status", label: "Status" },
      ], rowLabels: ["Balanced", "Unbalanced windward", "Unbalanced leeward", "Roof-step drift", "Obstruction drift"] },
    ],
    assumptions: ["Each roof plane and adjacency is identified by the geometry handoff.", "Screened-out phenomena remain visible with their reason."],
    deferred: ["Automation geometry binding", "CAD/PDF review rendering"],
  },
  {
    id: "seismic_elf",
    name: "Seismic — Equivalent Lateral Force",
    shortName: "Seismic ELF",
    family: "Seismic",
    standard: "ASCE 7-22 Ch. 11–12",
    description: "ELF base shear, vertical force distribution, story shear, and axis-specific SFRS cases.",
    visual: "seismic-stories",
    sourceContract: "loading_engine.seismic.ElfCalculatorOutput",
    cases: ["Principal axis X", "Principal axis Y"],
    inputs: [
      { ...hazard, fields: [
        { key: "S1", label: "S1", type: "number", value: 0.2 },
        { key: "SDS", label: "SDS", type: "number", value: 0.42 },
        { key: "SD1", label: "SD1", type: "number", value: 0.18 },
        { key: "TL", label: "Long-period transition TL", type: "number", unit: "s", value: 8 },
        { key: "siteClass", label: "Site class", type: "select", options: ["A", "B", "C", "D", "E"], value: "D" },
        { key: "riskCategory", label: "Risk category", type: "select", options: ["I", "II", "III", "IV"], value: "II" },
        { key: "importanceFactor", label: "Importance Ie", type: "number", value: 1 },
        { key: "sdc", label: "Seismic design category", type: "select", options: ["A", "B", "C", "D", "E", "F"], value: "C" },
      ] },
      { ...geometry, fields: [
        { key: "stories", label: "Story mass model", type: "table", addLabel: "Add story", minimumRows: 1, value: [
          { storyLabel: "Level 1", height_ft: 10, weight_lb: 80000 },
          { storyLabel: "Level 2", height_ft: 20, weight_lb: 80000 },
          { storyLabel: "Roof", height_ft: 30, weight_lb: 80000 },
        ], columns: [
          { key: "storyLabel", label: "Story", type: "text", value: "Level" },
          { key: "height_ft", label: "Elevation", type: "number", unit: "ft", value: 10 },
          { key: "weight_lb", label: "Seismic weight", type: "number", unit: "lb", value: 80000 },
        ] },
      ] },
      { id: "sfrs", label: "SFRS & period", summary: "System coefficients and period basis", openByDefault: true, fields: [
        { key: "axes", label: "Principal axes", type: "table", addLabel: "Add axis", minimumRows: 1, value: [
          { axis: "X", sfrs: "A.16 - Light-frame (wood) walls sheathed with wood structural panels rated for shear resistance", coefficientSource: "Catalog", R: 6.5, Cd: 4, omega0: 3, periodMode: "Approximate Ta", period_seconds: 0.3 },
          { axis: "Y", sfrs: "A.16 - Light-frame (wood) walls sheathed with wood structural panels rated for shear resistance", coefficientSource: "Catalog", R: 6.5, Cd: 4, omega0: 3, periodMode: "Approximate Ta", period_seconds: 0.3 },
        ], columns: [
          { key: "axis", label: "Axis", type: "select", options: ["X", "Y"], value: "X" },
          // Mirrors the engine's ASCE 7-22 Table 12.2-1 catalog. The engine owns
          // this list (sfrs_catalog_options in adapters/load_calculations.py) and
          // a test fails if these drift apart. The key prefix disambiguates
          // systems that share a name across categories (A.16 vs B.22).
          { key: "sfrs", label: "SFRS", type: "select", options: ["A.1 - Special reinforced concrete shear walls", "A.6 - Intermediate precast shear walls", "A.8 - Special reinforced masonry shear walls", "A.16 - Light-frame (wood) walls sheathed with wood structural panels rated for shear resistance", "A.17 - Light-frame (cold-formed steel) walls sheathed with wood structural panels or steel sheets", "A.18 - Light-frame walls with shear panels of all other materials", "A.19 - Light-frame (cold-formed steel) wall systems using flat strap bracing", "B.2 - Steel special concentrically braced frames", "B.3 - Steel ordinary concentrically braced frames", "B.4 - Special reinforced concrete shear walls", "B.22 - Light-frame (wood) walls sheathed with wood structural panels rated for shear resistance", "B.23 - Light-frame (wood) walls sheathed with shear panels of other materials", "G.1 - Steel special cantilever column systems", "G.2 - Steel ordinary cantilever column systems", "G.3 - Special reinforced concrete moment frames", "G.6 - Timber frames", "Other / deferred"], value: "A.16 - Light-frame (wood) walls sheathed with wood structural panels rated for shear resistance" },
          { key: "coefficientSource", label: "Coefficients", type: "select", options: ["Catalog", "Manual coefficients"], value: "Catalog" },
          { key: "R", label: "R", type: "number", value: 6.5 },
          { key: "Cd", label: "Cd", type: "number", value: 4 },
          { key: "omega0", label: "Omega0", type: "number", value: 3 },
          { key: "periodMode", label: "Period basis", type: "select", options: ["Approximate Ta", "User period"], value: "Approximate Ta" },
          { key: "period_seconds", label: "User period", type: "number", unit: "s", value: 0.3 },
        ] },
      ] },
    ],
    intermediates: ["SDC determination", "Period used", "Governing Cs", "Base shear by axis", "Story weight-height terms"],
    results: [{ id: "story-forces", label: "Story force distribution", description: "ELF forces and cumulative story shear for the active principal axis.", columns: [
      { key: "axis", label: "Axis" }, { key: "level", label: "Level" }, { key: "height", label: "hx", unit: "ft" }, { key: "weight", label: "wx", unit: "lb" }, { key: "cvx", label: "Cvx" }, { key: "fx", label: "Fx", unit: "lb" }, { key: "vx", label: "Vx", unit: "lb" },
    ], rowLabels: ["Roof", "Level 3", "Level 2", "Level 1"] }],
    assumptions: ["The approved mass model supplies story weights and elevations.", "X and Y axes retain separate SFRS and period results."],
    deferred: ["Automation mass-model binding", "CAD/PDF review rendering"],
  },
  {
    id: "dead_gravity",
    name: "Dead & Gravity Loads",
    shortName: "Dead / gravity",
    family: "Gravity",
    standard: "ASCE 7-22 Ch. 3",
    description: "Plan-based gravity regions, assembly dead loads, and tributary summaries.",
    visual: "plan-regions",
    sourceContract: "design_load_handoff roofGravityLoads / floorLoads",
    cases: ["Roof", "Floor", "Deck", "Ceiling"],
    inputs: [
      { id: "region", label: "Load region", summary: "Surface identity and extent", openByDefault: true, fields: [
        { key: "regionLabel", label: "Region label", type: "text", value: "Floor A" },
        { key: "surfaceKind", label: "Surface type", type: "select", options: ["Roof", "Floor", "Deck", "Ceiling"], value: "Floor" },
        { key: "length_ft", label: "Length", type: "number", unit: "ft", value: 28 },
        { key: "width_ft", label: "Width", type: "number", unit: "ft", value: 18 },
        { key: "level", label: "Level", type: "text", value: "Level 2" },
      ] },
      { id: "assembly", label: "Assembly", summary: "Dead-load components", openByDefault: true, fields: [
        { key: "structure_psf", label: "Structure", type: "number", unit: "psf", value: 4 },
        { key: "finish_psf", label: "Finish", type: "number", unit: "psf", value: 3 },
        { key: "ceiling_psf", label: "Ceiling", type: "number", unit: "psf", value: 2 },
        { key: "mep_psf", label: "MEP allowance", type: "number", unit: "psf", value: 1 },
        { key: "partition_psf", label: "Partition allowance", type: "number", unit: "psf", value: 0 },
      ] },
      { id: "tributary", label: "Tributary mapping", summary: "Review-only geometry binding", fields: [
        { key: "tributaryMode", label: "Tributary source", type: "select", options: ["Manual region", "Automation geometry", "Linked calculation"], value: "Manual region" },
        { key: "includeInWeight", label: "Include in seismic weight", type: "bool", value: true },
      ] },
    ],
    intermediates: ["Assembly component sum", "Region area", "Total region weight", "Tributary load routes", "Seismic-weight treatment"],
    results: [{ id: "gravity-regions", label: "Gravity region schedule", description: "Unfactored surface loads and total region weights.", columns: [
      { key: "region", label: "Region" }, { key: "level", label: "Level" }, { key: "kind", label: "Surface" }, { key: "area", label: "Area", unit: "ft²" }, { key: "dead", label: "Dead", unit: "psf" }, { key: "weight", label: "Weight", unit: "lb" }, { key: "seismicWeight", label: "Seismic W" }, { key: "source", label: "Source" },
    ], rowLabels: ["Primary region", "Adjacent region", "Opening deduction"] }],
    assumptions: ["Loads remain unfactored in the handoff.", "Openings and exclusions require explicit geometry or an engineer-entered adjustment."],
    deferred: ["Layer assembly library", "Plan/CAD geometry binding"],
  },
  {
    id: "roof_live",
    name: "Roof Live Load",
    shortName: "Roof live",
    family: "Occupancy",
    standard: "ASCE 7-22 §4.8",
    description: "Roof live-load basis with tributary-area and slope reduction review.",
    visual: "plan-regions",
    sourceContract: "stage5 RoofLiveLoadPolicy / roof_live_surface_loads",
    cases: ["Member tributary", "Region total"],
    inputs: [
      { id: "region", label: "Roof region", summary: "Plan extent and slope", openByDefault: true, fields: [
        { key: "regionLabel", label: "Region label", type: "text", value: "Roof A" },
        { key: "length_ft", label: "Length", type: "number", unit: "ft", value: 32 },
        { key: "width_ft", label: "Width", type: "number", unit: "ft", value: 18 },
        { key: "roofPitch", label: "Roof pitch", type: "number", unit: ":12", value: 6 },
      ] },
      { id: "basis", label: "Reduction basis", summary: "Lo, tributary area, and policy limits", openByDefault: true, fields: [
        { key: "Lo_psf", label: "Base roof live Lo", type: "number", unit: "psf", value: 20 },
        { key: "tributaryBasis", label: "Tributary basis", type: "select", options: ["Member standard spacing", "Region total"], value: "Member standard spacing" },
        { key: "memberSpacing_in", label: "Member spacing", type: "number", unit: "in o.c.", value: 24 },
        { key: "tributaryArea_sqft", label: "Tributary area", type: "number", unit: "ft²", value: 96 },
        { key: "minimum_psf", label: "Minimum Lr", type: "number", unit: "psf", value: 12 },
        { key: "maximum_psf", label: "Maximum Lr", type: "number", unit: "psf", value: 20 },
      ] },
    ],
    intermediates: ["Tributary-area factor R1", "Slope factor R2", "Unclamped Lr", "Minimum / maximum clamp"],
    results: [{ id: "roof-live", label: "Roof live-load regions", description: "Roof live load resolved for each selected surface basis.", columns: [
      { key: "region", label: "Region" }, { key: "area", label: "At", unit: "ft²" }, { key: "r1", label: "R1" }, { key: "r2", label: "R2" }, { key: "lr", label: "Lr", unit: "psf" }, { key: "basis", label: "Basis" },
    ], rowLabels: ["Selected roof region"] }],
    assumptions: ["Member-scale R1 is rechecked when actual tributary area becomes available."],
    deferred: ["Member selection binding", "CAD/PDF review rendering"],
  },
  {
    id: "floor_deck_live",
    name: "Floor & Deck Live Loads",
    shortName: "Floor / deck live",
    family: "Occupancy",
    standard: "ASCE 7-22 Table 4.3-1",
    description: "Plan regions for floor, deck, balcony, and occupancy live loads.",
    visual: "plan-regions",
    sourceContract: "stage5 FloorLiveLoadPolicy / design_load_handoff.floorLoads",
    cases: ["Interior floor", "Exterior deck", "Balcony"],
    inputs: [
      { id: "region", label: "Live-load region", summary: "Occupancy and plan extent", openByDefault: true, fields: [
        { key: "regionLabel", label: "Region label", type: "text", value: "Level 2 floor" },
        { key: "surfaceKind", label: "Surface type", type: "select", options: ["Interior floor", "Exterior deck", "Balcony"], value: "Interior floor" },
        { key: "occupancy", label: "Occupancy", type: "select", options: ["Residential", "Office", "Assembly", "Storage", "User-defined"], value: "Residential" },
        { key: "length_ft", label: "Length", type: "number", unit: "ft", value: 28 },
        { key: "width_ft", label: "Width", type: "number", unit: "ft", value: 18 },
        { key: "live_psf", label: "Live load", type: "number", unit: "psf", value: 40 },
      ] },
      { id: "limits", label: "Reduction & limits", summary: "Applicability and documented overrides", fields: [
        { key: "reductionAllowed", label: "Live-load reduction allowed", type: "bool", value: false },
        { key: "KLL", label: "Live-load element factor KLL", type: "number", value: 2 },
        { key: "tributaryArea_sqft", label: "Tributary area", type: "number", unit: "ft²", value: 504 },
        { key: "floorsSupported", label: "Floors supported", type: "number", value: 1 },
        { key: "minimum_psf", label: "Project minimum override", type: "number", unit: "psf", value: 0 },
        { key: "overrideReason", label: "Override reason", type: "text", value: "" , wide: true},
      ] },
    ],
    intermediates: ["Region area", "Occupancy basis", "Applicable reduction screen", "Total region live load"],
    results: [{ id: "live-regions", label: "Live-load region schedule", description: "Unfactored live loads by surface and occupancy.", columns: [
      { key: "region", label: "Region" }, { key: "surface", label: "Surface" }, { key: "occupancy", label: "Occupancy" }, { key: "area", label: "Area", unit: "ft²" }, { key: "live", label: "Live", unit: "psf" },
    ], rowLabels: ["Selected live-load region"] }],
    assumptions: ["Occupancy classification is explicit and reviewable.", "No live-load reduction is applied unless the engine result documents it."],
    deferred: ["Automated occupancy policy lookup", "Plan/CAD geometry binding"],
  },
  {
    id: "ceiling_attic_live",
    name: "Ceiling & Attic Live Loads",
    shortName: "Ceiling / attic",
    family: "Occupancy",
    standard: "IRC R301.5 / ASCE 7",
    description: "Ceiling base live load and attic-storage extents based on clear headroom.",
    visual: "attic-section",
    sourceContract: "stage5 CeilingLiveLoadPolicy / design_load_handoff.ceilingLoads",
    cases: ["Base ceiling", "Attic storage", "Porch ceiling"],
    inputs: [
      { id: "ceiling", label: "Ceiling region", summary: "Extent and roof relationship", openByDefault: true, fields: [
        { key: "regionLabel", label: "Region label", type: "text", value: "Upper ceiling" },
        { key: "ceilingWidth_ft", label: "Ceiling width", type: "number", unit: "ft", value: 28 },
        { key: "ceilingLength_ft", label: "Ceiling length", type: "number", unit: "ft", value: 40 },
        { key: "roofPitch", label: "Roof pitch", type: "number", unit: ":12", value: 6 },
        { key: "porchCeiling", label: "Porch / exterior ceiling", type: "bool", value: false },
      ] },
      { id: "live", label: "Live-load basis", summary: "Base and storage conditions", openByDefault: true, fields: [
        { key: "baseCeiling_psf", label: "Base ceiling live", type: "number", unit: "psf", value: 10 },
        { key: "atticStorage_psf", label: "Attic storage live", type: "number", unit: "psf", value: 20 },
        { key: "storageEnabled", label: "Attic storage enabled", type: "bool", value: true },
        { key: "headroomThreshold_in", label: "Headroom threshold", type: "number", unit: "in", value: 30 },
        { key: "porchMode", label: "Porch storage mode", type: "select", options: ["None", "R301.5 geometry", "Reduced uniform"], value: "None" },
        { key: "porchReducedStorage_psf", label: "Reduced porch storage", type: "number", unit: "psf", value: 10 },
      ] },
    ],
    intermediates: ["Headroom threshold", "Storage area", "Base-only area", "Total ceiling area"],
    results: [{ id: "attic-regions", label: "Ceiling and attic regions", description: "Base and storage live-load extents tied to the section graphic.", columns: [
      { key: "region", label: "Region" }, { key: "storageArea", label: "Storage area", unit: "ft²" }, { key: "baseOnlyArea", label: "Base-only area", unit: "ft²" }, { key: "headroom", label: "Min headroom", unit: "in" }, { key: "live", label: "Base / storage", unit: "psf" }, { key: "status", label: "Status" },
    ], rowLabels: ["Attic storage zone", "Left base-only zone", "Right base-only zone"] }],
    assumptions: ["Storage applies only where the engine-authored headroom clip meets the selected threshold."],
    deferred: ["Roof/ceiling geometry binding", "CAD/PDF review rendering"],
  },
  {
    id: "rain_ice",
    name: "Rain & Atmospheric Ice Loads",
    shortName: "Rain / ice",
    family: "Environmental",
    standard: "ASCE 7-22 Ch. 8 & 10",
    description: "Drainage head, ponding applicability, and atmospheric ice conditions for roof regions.",
    visual: "drainage",
    sourceContract: "stage5 rain_surface_loads / ice_surface_loads",
    cases: ["Rain", "Ponding review", "Atmospheric ice"],
    inputs: [
      { ...geometry, fields: [
        { key: "regionLabel", label: "Roof region", type: "text", value: "Low roof" },
        { key: "length_ft", label: "Length", type: "number", unit: "ft", value: 36 },
        { key: "width_ft", label: "Width", type: "number", unit: "ft", value: 24 },
        { key: "slope_percent", label: "Roof slope", type: "number", unit: "%", value: 2 },
        { key: "drainCount", label: "Primary drains", type: "number", value: 2 },
        { key: "overflowCount", label: "Overflow drains", type: "number", value: 2 },
      ] },
      { id: "rain", label: "Rain & drainage", summary: "Hydraulic head inputs", openByDefault: true, fields: [
        { key: "riskCategory", label: "Risk category", type: "select", options: ["I", "II", "III", "IV"], value: "II" },
        { key: "rainfallIntensity15Min_in_per_hr", label: "15-minute design rainfall", type: "number", unit: "in/hr", value: 5 },
        { key: "ds_in", label: "Static head ds", type: "number", unit: "in", value: 1.5 },
        { key: "dh_in", label: "Hydraulic head dh", type: "number", unit: "in", value: 1 },
        { key: "dp_in", label: "Ponding head dp", type: "number", unit: "in", value: 0 },
        { key: "pondingAnalysisComplete", label: "Ponding analysis complete", type: "bool", value: false },
      ] },
      { id: "ice", label: "Atmospheric ice", summary: "Hazard and exposure conditions", openByDefault: true, fields: [
        { key: "iceApplicable", label: "Ice hazard applicable", type: "bool", value: false },
        { key: "nominalThickness_in", label: "Nominal ice thickness", type: "number", unit: "in", value: 0 },
        { key: "iceDensity_pcf", label: "Ice density", type: "number", unit: "pcf", value: 57 },
        { key: "iceImportance", label: "Ice importance factor", type: "number", value: 1 },
      ] },
    ],
    intermediates: ["Design rainfall intensity", "Static water head", "Hydraulic head", "Ponding head", "Rain pressure R", "Ponding applicability", "Design ice thickness"],
    results: [{ id: "environmental", label: "Environmental roof loads", description: "Rain and ice results by roof region and condition.", columns: [
      { key: "region", label: "Region" }, { key: "condition", label: "Condition" }, { key: "pressure", label: "Pressure", unit: "psf" }, { key: "depth", label: "Depth", unit: "in" }, { key: "status", label: "Status" }, { key: "source", label: "Source" },
    ], rowLabels: ["Rain", "Ponding", "Atmospheric ice"] }],
    assumptions: ["Drainage capacity and secondary drainage remain project inputs.", "Inactive hazards stay visible as not applicable rather than disappearing."],
    deferred: ["Drainage system sizing", "CAD/PDF review rendering"],
  },
  {
    id: "wall_gravity",
    name: "Wall Gravity Loads",
    shortName: "Wall gravity",
    family: "Gravity",
    standard: "ASCE 7-22 Ch. 3",
    description: "Wall-face dead load converted to top, base, and opening-adjusted line loads.",
    visual: "wall-elevation",
    sourceContract: "stage5 WallGravityLoadPolicy / design_load_handoff.wallFaceLoads",
    cases: ["Uniform wall", "Tapered / gable wall", "Opening-adjusted"],
    inputs: [
      { id: "wall", label: "Wall face", summary: "Classification and elevation geometry", openByDefault: true, fields: [
        { key: "wallLabel", label: "Wall label", type: "text", value: "North wall" },
        { key: "level", label: "Level", type: "text", value: "Level 2" },
        { key: "wallType", label: "Wall construction", type: "select", options: ["Exterior wood frame", "Interior wood frame", "Exterior CFS", "Interior CFS", "CMU", "User-defined"], value: "Exterior wood frame" },
        { key: "length_ft", label: "Wall length", type: "number", unit: "ft", value: 30 },
        { key: "heightLeft_ft", label: "Left height", type: "number", unit: "ft", value: 9 },
        { key: "heightRight_ft", label: "Right height", type: "number", unit: "ft", value: 9 },
      ] },
      { id: "weight", label: "Weight & openings", summary: "Face weight and deductions", openByDefault: true, fields: [
        { key: "unitWeight_psf", label: "Wall face weight", type: "number", unit: "psf", value: 8 },
        { key: "openingArea_sqft", label: "Opening area", type: "number", unit: "ft²", value: 36 },
        { key: "openings", label: "Opening schedule", type: "table", addLabel: "Add opening", value: [], columns: [
          { key: "label", label: "Opening", type: "text", value: "Window" },
          { key: "width_ft", label: "Width", type: "number", unit: "ft", value: 3 },
          { key: "height_ft", label: "Height", type: "number", unit: "ft", value: 4 },
          { key: "count", label: "Count", type: "number", value: 1 },
        ] },
        { key: "minimum_plf_per_ft", label: "Minimum per ft height", type: "number", unit: "plf/ft", value: 5 },
        { key: "maximum_plf_per_ft", label: "Maximum per ft height", type: "number", unit: "plf/ft", value: 100 },
        { key: "tapered", label: "Tapered / gable face", type: "bool", value: false },
      ] },
    ],
    intermediates: ["Gross wall-face area", "Opening deduction", "Net wall weight", "Low- and high-end line-load distribution", "Policy limit checks"],
    results: [{ id: "walls", label: "Wall gravity schedule", description: "Wall-face and line-load values for downstream framing and seismic weight.", columns: [
      { key: "wall", label: "Wall" }, { key: "level", label: "Level" }, { key: "construction", label: "Construction" }, { key: "profile", label: "Profile" }, { key: "length", label: "Length", unit: "ft" }, { key: "average", label: "Average", unit: "plf" }, { key: "top", label: "Low end", unit: "plf" }, { key: "base", label: "High end", unit: "plf" },
    ], rowLabels: ["Selected wall face"] }],
    assumptions: ["Wall gravity remains separate from lateral wind pressure.", "Tapered faces expose low- and high-end line loads instead of a single uniform value."],
    deferred: ["Opening geometry binding", "CAD/PDF review rendering"],
  },
] as const;

export const LOAD_CALC_TEMPLATE_BY_ID = Object.fromEntries(
  LOAD_CALC_TEMPLATES.map((template) => [template.id, template]),
) as Record<LoadCalcTemplateId, LoadCalcTemplate>;

export function isLoadCalcTemplateId(value: string | undefined): value is LoadCalcTemplateId {
  return !!value && Object.prototype.hasOwnProperty.call(LOAD_CALC_TEMPLATE_BY_ID, value);
}

export function defaultLoadCalcValues(template: LoadCalcTemplate): LoadCalcValues {
  const values: LoadCalcValues = {};
  for (const section of template.inputs) {
    for (const field of section.fields) {
      if (field.value !== undefined) values[field.key] = field.value;
    }
  }
  return values;
}

export const LOAD_CALCULATOR_PREFIX = "load_";

const LOAD_MARK_PREFIX: Record<LoadCalcTemplateId, string> = {
  wind_mwfrs: "WM",
  wind_cc: "WC",
  snow: "SN",
  seismic_elf: "EQ",
  dead_gravity: "DG",
  roof_live: "RL",
  floor_deck_live: "FL",
  ceiling_attic_live: "AL",
  rain_ice: "RI",
  wall_gravity: "WG",
};

export function loadCalculatorId(templateId: LoadCalcTemplateId): string {
  return `${LOAD_CALCULATOR_PREFIX}${templateId}`;
}

export function loadTemplateIdFromCalculatorId(
  calculatorId: string | null | undefined,
): LoadCalcTemplateId | null {
  if (!calculatorId?.startsWith(LOAD_CALCULATOR_PREFIX)) return null;
  const candidate = calculatorId.slice(LOAD_CALCULATOR_PREFIX.length);
  return isLoadCalcTemplateId(candidate) ? candidate : null;
}

export function isLoadCalculatorId(calculatorId: string | null | undefined): boolean {
  return loadTemplateIdFromCalculatorId(calculatorId) != null;
}

export function nextLoadCalcMark(
  templateId: LoadCalcTemplateId,
  existingNames: readonly string[],
): string {
  const prefix = LOAD_MARK_PREFIX[templateId];
  const used = new Set(
    existingNames
      .map((name) => name.trim().toUpperCase())
      .filter((name) => name.startsWith(`${prefix}-`)),
  );
  let index = 1;
  while (used.has(`${prefix}-${index}`)) index += 1;
  return `${prefix}-${index}`;
}
